import React, { useCallback, useState } from 'react';

import AddModal from './add_modal';
import CorrectForm from './correct_form';
import MonthPassModal from './month_pass_modal';
import NoteActionModal from './note_action_modal';
import PassDialog from './pass_dialog';
import WaiveForm from './waive_form';
import {
  post_penalty_add,
  post_penalty_correct,
  post_penalty_free_pass,
  post_penalty_month_pass,
  post_penalty_remove,
  post_penalty_revert,
  post_penalty_waive
} from '../api';
import { month_label } from '../format';
import { free_pass_view, mutation_result, row_view } from './adapters';
import { KIND, error_view, reloads_list } from './error_handlers';
import { ROW_ACTION } from './params';
import {
  add_payload,
  correct_payload,
  free_pass_payload,
  month_pass_payload,
  remove_payload,
  revert_payload,
  waive_payload
} from './requests';

/*
 * PROD-7828 R6 — the single-penalty write dialogs and their one write path, shared by the Penalties tab
 * and the session page's Change penalty modal, so both send the same requests and show a refusal inside
 * the dialog that sent it (B7). The caller owns what a save refreshes: on_saved(result) after a write,
 * on_reload() when a refusal means the rows on screen are stale.
 */
export const useRowWrite = ({ on_saved, on_reload, on_permission }) => {
  const [dialog, setDialog] = useState(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState(null);

  const open = useCallback((next) => {
    setError(null);
    setDialog(next);
  }, []);
  const close = useCallback(() => {
    setDialog(null);
    setError(null);
  }, []);

  const write = (send, opts = {}) => {
    if (busy) return;
    setBusy(true);
    setError(null);
    send()
      .then((data) => {
        setDialog(null);
        on_saved(mutation_result(data));
      })
      .catch((e) => {
        const v = error_view(e, { write: true, add: !!opts.add });
        if (v.kind === KIND.PERMISSION) {
          on_permission(e);
          return;
        }
        // A version conflict or a changed directive carries the server's current state: the dialog adopts it.
        if (v.kind === KIND.ADOPT) {
          setDialog(
            (d) =>
              d && {
                ...d,
                row: v.current ? row_view(v.current) : d.row,
                state: v.free_pass ? free_pass_view(v.free_pass) : d.state
              }
          );
        }
        setError(v);
        if (reloads_list(v)) on_reload();
      })
      .finally(() => setBusy(false));
  };

  return { dialog, open, close, busy, error, write };
};

// The dialog a row action opens; the free-pass actions need the teacher-month's state, so the caller builds those.
export const row_dialog = (action, row) => {
  if (action === ROW_ACTION.CORRECT) return { kind: 'correct', row };
  if (action === ROW_ACTION.WAIVE) return { kind: 'waive', row, waive: true };
  if (action === ROW_ACTION.UNWAIVE) return { kind: 'waive', row, waive: false };
  if (action === ROW_ACTION.REVERT || action === ROW_ACTION.REMOVE) {
    return { kind: 'note', action, row };
  }
  return null;
};

/*
 * w: useRowWrite's return. add: the Add dialog's props (new_request_id, search_teachers,
 * search_sessions(teacher_uuid, q, month), seed); pay_month / month_text: the month an added penalty is
 * charged in, unless the Add dialog was moved to the month a refusal suggested (d.month, CD-184). The
 * session lookup searches that same month, so the body and the lookup agree (CD-179).
 */
export const RowDialogs = ({ w, pay_month, month_text, add, on_focus_lost }) => {
  const { dialog: d, busy, error, write, close } = w;
  if (!d) return null;
  const common = { saving: busy, error, on_close: close, on_focus_lost };
  if (d.kind === 'correct') {
    return (
      <CorrectForm
        {...common}
        row={d.row}
        on_submit={(form) => write(() => post_penalty_correct(correct_payload(d.row.row, form)))}
      />
    );
  }
  if (d.kind === 'waive') {
    return (
      <WaiveForm
        {...common}
        row={d.row}
        waive={d.waive}
        on_submit={(form) => write(() => post_penalty_waive(waive_payload(d.row.row, form)))}
      />
    );
  }
  if (d.kind === 'note') {
    return (
      <NoteActionModal
        {...common}
        action={d.action}
        row={d.row}
        on_submit={(note) =>
          write(() =>
            d.action === ROW_ACTION.REVERT
              ? post_penalty_revert(revert_payload(d.row.row, note))
              : post_penalty_remove(remove_payload(d.row.row, note))
          )
        }
      />
    );
  }
  if (d.kind === 'pass') {
    return (
      <PassDialog
        {...common}
        action={d.action}
        state={d.state}
        day={d.day}
        teacher_name={d.teacher_name}
        on_submit={(req, confirm_displace) =>
          write(() => post_penalty_free_pass(free_pass_payload(d.state.raw, req, confirm_displace)))
        }
      />
    );
  }
  if (d.kind === 'month_pass') {
    return (
      <MonthPassModal
        {...common}
        action={d.action}
        teacher_name={d.teacher_name}
        month_text={d.month_text}
        on_submit={(note) =>
          write(() =>
            post_penalty_month_pass(month_pass_payload(d.teacher_uuid, d.pay_month, d.action, note))
          )
        }
      />
    );
  }
  if (d.kind === 'add') {
    const month = d.month || pay_month;
    return (
      <AddModal
        {...common}
        month_text={d.month ? month_label(d.month) : month_text}
        new_request_id={add.new_request_id}
        search_teachers={add.search_teachers}
        search_sessions={(teacher_uuid, q) => add.search_sessions(teacher_uuid, q, month)}
        default_pcts={add.default_pcts}
        seed={add.seed}
        on_move={(month) => w.open({ ...d, month })}
        on_submit={(form, confirms) =>
          write(() => post_penalty_add(add_payload(month, form, confirms)), {
            add: true
          })
        }
      />
    );
  }
  return null;
};
